/**
 * Multisampled render targets at the samples this renderer's device draws for a Godot MSAA mode.
 * Godot keeps the counts both the colour and the depth attachments support, for the format each
 * really has (`rendering_device_driver_vulkan.cpp:1999`). This takes the colour storage from the
 * target's own texture, as three allocates it, and the depth storage DEPTH_COMPONENT24.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { msaaSampleCount, supportedSampleCount } from '../godot/rendering/msaa';
import { useDisposeOnRelease } from './useDisposeOnRelease';
import { perObject } from '../utils/perObject';
/**
 * The target `create` builds, multisampled at the count the device draws for `mode` on that target's
 * storage. `create` is stable while the target stays valid. The hook owns the target and disposes it.
 */
export function useMultisampledTarget(mode, create) {
    const gl = useThree((state) => state.gl);
    const target = useMemo(() => {
        const built = create();
        built.samples = deviceSampleCount(gl, mode, colourStorage(built.texture));
        return built;
    }, [gl, mode, create]);
    useDisposeOnRelease(target);
    return target;
}
/**
 * Three's `getInternalFormat` for an RGBA texture (`WebGLTextures.js:129`). A target that takes output
 * like the drawing buffer stores an 8-bit sRGB tag linear (`:2120`), so such a target is linear or
 * pins its format, as `CanvasFrame` pins RGBA8.
 */
function colourStorage(texture) {
    if (texture.internalFormat !== null)
        return texture.internalFormat;
    if (texture.type === THREE.FloatType)
        return 'RGBA32F';
    if (texture.type === THREE.HalfFloatType)
        return 'RGBA16F';
    if (texture.type !== THREE.UnsignedByteType) {
        throw new Error(`expected an unsigned byte, half float or float target, got texel type ${texture.type}`);
    }
    const isSrgb = THREE.ColorManagement.getTransfer(texture.colorSpace) === THREE.SRGBTransfer;
    return isSrgb ? 'SRGB8_ALPHA8' : 'RGBA8';
}
/**
 * The counts colour and depth share, per context and colour storage. Filled only by
 * `sharedSampleCounts`. A context lists the same counts for its life, and the entry goes with the
 * context.
 */
const sharedCountsOf = perObject(() => new Map());
/** 0, one sample per pixel, where the context lists no counts: WebGL 1 has no multisampled target. */
function deviceSampleCount(gl, mode, storage) {
    const requested = msaaSampleCount(mode);
    const context = gl.getContext();
    if (requested === 0 || !('getInternalformatParameter' in context))
        return 0;
    return supportedSampleCount(requested, sharedSampleCounts(context, storage));
}
function sharedSampleCounts(context, storage) {
    const perStorage = sharedCountsOf(context);
    const cached = perStorage.get(storage);
    if (cached)
        return cached;
    const depthCounts = renderbufferSampleCounts(context, context.DEPTH_COMPONENT24);
    const colourFormat = context[storage];
    const shared = renderbufferSampleCounts(context, colourFormat).filter((count) => depthCounts.includes(count));
    // A lost context lists no counts, and would keep MSAA off after the browser restores it.
    if (!context.isContextLost())
        perStorage.set(storage, shared);
    return shared;
}
function renderbufferSampleCounts(context, format) {
    const counts = context.getInternalformatParameter(context.RENDERBUFFER, format, context.SAMPLES);
    return Array.from(counts ?? []);
}
//# sourceMappingURL=multisampledTarget.js.map